import React from 'react'
import { 
  XMarkIcon,
  ClockIcon,
  CheckCircleIcon,
  DocumentCheckIcon,
  FlagIcon,
  UserIcon,
  CalendarIcon
} from '@heroicons/react/24/outline'

interface TemplatePreviewProps {
  template: any
  onClose: () => void
}

export const TemplatePreview: React.FC<TemplatePreviewProps> = ({ template, onClose }) => {
  const getProjectTypeIcon = (type: string) => {
    switch (type) {
      case 'insurance_policy': return '🛡️'
      case 'investment_transfer': return '💼'
      case 'estate_planning': return '📋'
      case 'tax_planning': return '📊'
      case 'retirement_planning': return '🎯'
      case 'education_planning': return '🎓'
      case 'business_planning': return '🏢'
      default: return '📁'
    }
  }

  const getTaskTypeColor = (type: string) => {
    switch (type) {
      case 'milestone': return 'bg-green-100 text-green-800 border-green-200'
      case 'decision': return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      default: return 'bg-blue-100 text-blue-800 border-blue-200'
    }
  }

  const calculateCumulativeDays = () => {
    let cumulativeDays = 0
    const timeline: any[] = []

    // Add milestones to timeline
    if (template.milestones?.length > 0) {
      template.milestones.forEach((milestone: any) => {
        timeline.push({
          type: 'milestone',
          name: milestone.name,
          description: milestone.description,
          duration: milestone.duration_days,
          startDay: cumulativeDays,
          endDay: cumulativeDays + milestone.duration_days
        })
        cumulativeDays += milestone.duration_days
      })
    }

    return { timeline, totalDays: cumulativeDays }
  }

  const { timeline, totalDays } = calculateCumulativeDays()

  return (
    <div className="fixed z-50 inset-0 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" onClick={onClose}>
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          {/* Header */}
          <div className="bg-gray-50 px-4 py-4 sm:px-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-3xl mr-3">
                  {template.icon || getProjectTypeIcon(template.project_type)}
                </span>
                <div>
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    {template.name}
                  </h3>
                  <p className="text-sm text-gray-500 capitalize">
                    {template.project_type?.replace(/_/g, ' ')}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-4 py-5 sm:p-6 max-h-[70vh] overflow-y-auto">
            {/* Summary */}
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Overview</h4>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div className="flex items-center">
                    <CalendarIcon className="h-5 w-5 text-blue-600 mr-2" />
                    <div>
                      <p className="text-gray-600">Duration</p>
                      <p className="font-medium text-gray-900">{totalDays} days</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <FlagIcon className="h-5 w-5 text-blue-600 mr-2" />
                    <div>
                      <p className="text-gray-600">Milestones</p>
                      <p className="font-medium text-gray-900">{template.milestones?.length || 0}</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <CheckCircleIcon className="h-5 w-5 text-blue-600 mr-2" />
                    <div>
                      <p className="text-gray-600">Tasks</p>
                      <p className="font-medium text-gray-900">{template.tasks?.length || 0}</p>
                    </div>
                  </div>
                </div>
                {template.description && (
                  <p className="mt-3 text-gray-700">{template.description}</p>
                )}
              </div>
            </div>

            {/* Timeline */}
            {timeline.length > 0 && (
              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Project Timeline</h4>
                <div className="space-y-3">
                  {timeline.map((item, index) => (
                    <div key={index} className="relative">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <div className="h-8 w-8 rounded-full bg-green-500 flex items-center justify-center">
                            <span className="text-white text-xs font-medium">{index + 1}</span>
                          </div>
                        </div>
                        <div className="ml-4 flex-1">
                          <div className="bg-white portal-content-shadow border border-gray-200 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-1">
                              <h5 className="text-sm font-medium text-gray-900">{item.name}</h5>
                              <span className="text-xs text-gray-500">
                                Days {item.startDay + 1}-{item.endDay}
                              </span>
                            </div>
                            {item.description && (
                              <p className="text-sm text-gray-600">{item.description}</p>
                            )}
                            <div className="mt-2 flex items-center text-xs text-gray-500">
                              <ClockIcon className="h-4 w-4 mr-1" />
                              {item.duration} days
                            </div>
                          </div>
                        </div>
                      </div>
                      {index < timeline.length - 1 && (
                        <div className="absolute left-4 top-8 bottom-0 w-0.5 bg-gray-300"></div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tasks */}
            {template.tasks && template.tasks.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">Tasks & Activities</h4>
                <div className="space-y-2">
                  {template.tasks.map((task: any, index: number) => (
                    <div
                      key={index}
                      className={`border rounded-lg p-3 ${getTaskTypeColor(task.task_type)}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h5 className="text-sm font-medium">{task.title}</h5>
                          {task.description && (
                            <p className="text-xs mt-1 opacity-75">{task.description}</p>
                          )}
                        </div>
                        <div className="flex items-center space-x-2 ml-4">
                          {task.compliance_required && (
                            <DocumentCheckIcon className="h-4 w-4" title="Compliance Required" />
                          )}
                          {task.requires_signature && (
                            <UserIcon className="h-4 w-4" title="Requires Signature" />
                          )}
                          <span className="text-xs whitespace-nowrap">
                            {task.duration_days} day{task.duration_days !== 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={onClose}
              className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}